import { DateRulesEditor } from './DateRulesEditor';
import { IconArrowUp, IconCalendar, IconMapPin, IconMinus, IconSignature, IconTable } from '@tabler/icons-react';
import type { FormField, FormFieldMatrixItem, FormFieldOption } from '../../types';
import { generateFieldId } from '../../lib/form-templates';
import { PlusIcon } from '../icons';
import { QuestionImageEditor } from './QuestionImageEditor';

/** Props for previewing or configuring one expanded structured question. */
export interface ExpandedQuestionEditorProps {
  field: ExpandedQuestionField;
  onChange: (patch: Partial<FormField>) => void;
  /** The canvas edits content; the disclosed panel owns behavior settings. */
  mode: 'canvas' | 'settings';
  workspaceId?: string;
  /** Only preceding date fields can define a dependent limit. */
  earlierFields?: readonly FormField[];
  committedImageKeys?: ReadonlySet<string>;
}

/** New visual question variants with dedicated content and settings surfaces. */
export type ExpandedQuestionField = Extract<FormField, { type: 'checkbox' | 'image_choice' | 'ranking' | 'date_time' | 'matrix' | 'address' | 'signature' | 'image_click_map' }>;

/** Narrow a form field before rendering its expanded inline editor. */
export function isExpandedQuestionField(field: FormField): field is ExpandedQuestionField {
  return ['checkbox', 'image_choice', 'ranking', 'date_time', 'matrix', 'address', 'signature', 'image_click_map'].includes(field.type);
}

const control = 'min-h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';
const iconButton = 'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt disabled:cursor-default disabled:opacity-30';
const textButton = 'inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';

function newOption(): FormFieldOption {
  const id = generateFieldId();
  return { id, label: '', value: `option_${id}` };
}

function newMatrixItem(): FormFieldMatrixItem {
  return { id: generateFieldId(), label: '' };
}

/** Compact answer-shape preview shared by expanded types without inline behavior controls. */
function PreviewRow({ name, example, label, icon }: { name: string; example: string; label: string; icon: React.ReactNode }): React.ReactElement {
  return <div data-testid="field-card-preview" role="group" aria-label={`${name} answer preview`}
    className="mt-2 flex min-w-0 items-start gap-3 rounded-lg border border-border/60 bg-background/50 px-3 py-2 text-sm text-muted-foreground">
    <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0">{icon}</span>
    <span className="min-w-0 flex-1 whitespace-pre-wrap [overflow-wrap:anywhere]">{example}</span>
    <span className="shrink-0">{label}</span>
  </div>;
}

interface ListEditorProps {
  label: string;
  items: Array<{ id: string; label: string }>;
  itemLabel: string;
  onChange: (items: Array<{ id: string; label: string }>) => void;
  onAdd: () => void;
}

/** Compact stable-identity rows shared by ranking and matrix authoring. */
function ListEditor({ label, items, itemLabel, onChange, onAdd }: ListEditorProps): React.ReactElement {
  return <fieldset className="space-y-2">
    <legend className="mb-2 text-sm text-muted-foreground">{label}</legend>
    {items.map((item, index) => <div key={item.id} className="flex items-center gap-1">
      <input aria-label={`${itemLabel} ${index + 1}`} value={item.label} placeholder={itemLabel} className={control}
        onChange={event => onChange(items.map(candidate => candidate.id === item.id ? { ...candidate, label: event.target.value } : candidate))} />
      <button type="button" aria-label={`Move ${itemLabel.toLocaleLowerCase()} ${index + 1} up`} className={iconButton} disabled={index === 0}
        onClick={() => { const next = [...items]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; onChange(next); }}>
        <IconArrowUp aria-hidden="true" className="h-4 w-4" />
      </button>
      <button type="button" aria-label={`Remove ${itemLabel.toLocaleLowerCase()} ${index + 1}`} className={iconButton}
        onClick={() => onChange(items.filter(candidate => candidate.id !== item.id))}>
        <IconMinus aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>)}
    <button type="button" aria-label={`Add ${itemLabel.toLocaleLowerCase()}`} className={textButton}
      onClick={onAdd}><PlusIcon aria-hidden="true" className="h-4 w-4" />Add {itemLabel.toLocaleLowerCase()}</button>
  </fieldset>;
}

/** Separates compact canvas content from behavior owned by the Edit disclosure. */
export function ExpandedQuestionEditor({ field, onChange, mode, workspaceId, earlierFields,
  committedImageKeys }: ExpandedQuestionEditorProps): React.ReactElement | null {
  if (mode === 'canvas') {
    if (field.type === 'checkbox') {
      return <div role="group" aria-label="Checkbox answer preview" className="mt-3">
        <label className="flex min-h-11 items-center gap-3 rounded-lg bg-surface px-4 py-3 text-sm">
          <input type="checkbox" disabled className="h-4 w-4 accent-volt" />Confirm
        </label>
      </div>;
    }

    if (field.type === 'image_choice') {
      const patchOptions = (options: FormFieldOption[]) => onChange({ options });
      return <fieldset aria-label="Image choice answer preview" className="mt-3 space-y-4">
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          {field.options.map((item, index) => <div key={item.id} className="space-y-2 rounded-lg border border-border bg-background p-3">
            <QuestionImageEditor image={item.image} label={`Option ${index + 1} image`} workspaceId={workspaceId}
              committedImageKeys={committedImageKeys} onChange={image => patchOptions(field.options.map(candidate => candidate.id === item.id ? { ...candidate, image } : candidate))} />
            <div className="flex items-center gap-1">
              <input aria-label={`Option ${index + 1}`} value={item.label} placeholder="Option" className={control}
                onChange={event => patchOptions(field.options.map(candidate => candidate.id === item.id ? { ...candidate, label: event.target.value } : candidate))} />
              <button type="button" aria-label={`Remove option ${index + 1}`} className={iconButton}
                onClick={() => patchOptions(field.options.filter(candidate => candidate.id !== item.id))}><IconMinus aria-hidden="true" className="h-4 w-4" /></button>
            </div>
          </div>)}
        </div>
        <button type="button" aria-label="Add option" className={textButton} onClick={() => patchOptions([...field.options, newOption()])}>
          <PlusIcon aria-hidden="true" className="h-4 w-4" />Add option
        </button>
      </fieldset>;
    }

    if (field.type === 'ranking') {
      return <div className="mt-3"><ListEditor label="Order" itemLabel="Option" items={field.options}
        onChange={items => onChange({ options: items.map(item => ({ ...item,
          value: field.options.find(option => option.id === item.id)?.value ?? `option_${item.id}` })) })}
        onAdd={() => onChange({ options: [...field.options, newOption()] })} />
      </div>;
    }

    if (field.type === 'date_time') {
      const presentation = field.dateTime.mode === 'time'
        ? { example: 'Choose a time', label: 'Time' }
        : field.dateTime.mode === 'date_time'
          ? { example: 'Choose a date and time', label: 'Date & time' }
          : { example: 'Choose a date', label: 'Date' };
      return <PreviewRow name="Date" {...presentation} icon={<IconCalendar className="h-4 w-4" />} />;
    }

    if (field.type === 'matrix') {
      const rowCount = field.matrix.rows.length;
      const columnCount = field.matrix.columns.length;
      return <PreviewRow name="Matrix" example={`${rowCount} ${rowCount === 1 ? 'row' : 'rows'} · ${columnCount} ${columnCount === 1 ? 'column' : 'columns'}`}
        label="Matrix" icon={<IconTable className="h-4 w-4" />} />;
    }

    if (field.type === 'address') {
      return <PreviewRow name="Address" example="Street address" label="Address" icon={<IconMapPin className="h-4 w-4" />} />;
    }

    if (field.type === 'signature') {
      return <PreviewRow name="Signature" example="Draw or type name" label="Signature" icon={<IconSignature className="h-4 w-4" />} />;
    }

    return <div role="group" aria-label="Image click map answer preview" className="mt-3">
      <QuestionImageEditor image={field.image} label="Map image" workspaceId={workspaceId} committedImageKeys={committedImageKeys} fit="contain"
        onChange={image => onChange({ image })} />
    </div>;
  }

  if (field.type === 'checkbox') {
    const link = field.checkbox?.link;
    return <div className="space-y-2">
      {link ? <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_auto] gap-2 max-sm:grid-cols-1">
        <input aria-label="Policy link label" placeholder="Policy label" value={link.label} className={control}
          onChange={event => onChange({ checkbox: { link: { ...link, label: event.target.value } } })} />
        <input aria-label="Policy link URL" placeholder="https://" type="url" value={link.url} className={control}
          onChange={event => onChange({ checkbox: { link: { ...link, url: event.target.value } } })} />
        <button type="button" className={iconButton} aria-label="Remove policy link" onClick={() => onChange({ checkbox: undefined })}><IconMinus aria-hidden="true" className="h-4 w-4" /></button>
      </div> : <button type="button" className={textButton} aria-label="Add policy link"
        onClick={() => onChange({ checkbox: { link: { label: '', url: '' } } })}><PlusIcon aria-hidden="true" className="h-4 w-4" />Add link</button>}
    </div>;
  }

  if (field.type === 'image_choice') {
    return <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-sm text-muted-foreground">Selection<select aria-label="Image selection" value={field.selectionMode}
          className={`${control} cursor-pointer`} onChange={event => {
            const selectionMode = event.target.value as 'single' | 'multiple';
            onChange(selectionMode === 'single'
              ? { selectionMode, minSelected: undefined, maxSelected: undefined }
              : { selectionMode });
          }}>
          <option value="single">One</option><option value="multiple">Many</option>
        </select></label>
        {field.selectionMode === 'multiple' ? <>
          <label className="grid gap-1 text-sm text-muted-foreground">Minimum<input aria-label="Minimum selections" type="number" min={1} max={field.options.length}
            className={control} value={field.minSelected ?? ''} onChange={event => onChange({ minSelected: event.target.value ? Number(event.target.value) : undefined })} /></label>
          <label className="grid gap-1 text-sm text-muted-foreground">Maximum<input aria-label="Maximum selections" type="number" min={1} max={field.options.length}
            className={control} value={field.maxSelected ?? ''} onChange={event => onChange({ maxSelected: event.target.value ? Number(event.target.value) : undefined })} /></label>
        </> : null}
    </div>;
  }

  if (field.type === 'ranking') {
    return null;
  }

  if (field.type === 'date_time') {
    return <div className="space-y-4">
      <label className="grid max-w-48 gap-1 text-sm text-muted-foreground">Date input
        <select aria-label="Date input" value={field.dateTime.mode} className={`${control} cursor-pointer`}
          onChange={event => onChange({ dateTime: { mode: event.target.value as 'date' | 'time' | 'date_time' } })}>
          <option value="date">Date</option><option value="time">Time</option><option value="date_time">Date and time</option>
        </select>
      </label>
      <DateRulesEditor key={field.dateTime.mode} config={field.dateTime} earlierFields={earlierFields}
        onChange={dateTime => onChange({ dateTime })} />
    </div>;
  }

  if (field.type === 'matrix') {
    return <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
      <label className="col-span-full space-y-2 text-sm text-muted-foreground">
        <span>Selections per row</span>
        <select value={field.selectionMode ?? 'single'} className={control}
          onChange={event => onChange({ selectionMode: event.target.value === 'multiple' ? 'multiple' : 'single' })}>
          <option value="single">One</option>
          <option value="multiple">Multiple</option>
        </select>
      </label>
      <ListEditor label="Rows" itemLabel="Row" items={field.matrix.rows}
        onChange={rows => onChange({ matrix: { ...field.matrix, rows } })}
        onAdd={() => onChange({ matrix: { ...field.matrix, rows: [...field.matrix.rows, newMatrixItem()] } })} />
      <ListEditor label="Columns" itemLabel="Column" items={field.matrix.columns}
        onChange={columns => onChange({ matrix: { ...field.matrix, columns } })}
        onAdd={() => onChange({ matrix: { ...field.matrix, columns: [...field.matrix.columns, newMatrixItem()] } })} />
    </div>;
  }

  if (field.type === 'address') {
    return <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg bg-surface px-4 py-3 text-sm">
      <input type="checkbox" aria-label="Require postal code" checked={field.address.requirePostalCode} className="h-4 w-4 accent-volt"
        onChange={event => onChange({ address: { requirePostalCode: event.target.checked } })} />Require postal code
    </label>;
  }

  if (field.type === 'signature') {
    return null;
  }

  return <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
      <label className="grid gap-1 text-sm text-muted-foreground">Minimum clicks<input type="number" min={1} className={control}
        value={field.minClicks ?? ''} onChange={event => onChange({ minClicks: event.target.value ? Number(event.target.value) : undefined })} /></label>
      <label className="grid gap-1 text-sm text-muted-foreground">Maximum clicks<input type="number" min={1} className={control}
        value={field.maxClicks ?? ''} onChange={event => onChange({ maxClicks: event.target.value ? Number(event.target.value) : undefined })} /></label>
    </div>
  </div>;
}
